import React, { useState, useEffect } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Brush, // ✅ Navigation Zoom/Scroll
} from "recharts";

const COLORS = {
    BTC: "#F7931A",
    ETH: "#627EEA",
    EGLD: "#23F7DD",
    PAXG: "#D4AF37",
    MATIC: "#8247E5",
    OP: "#FF0420",
    ENTQ: "#FF007A",
};

// Helper micro-jitter (pure display)
const jitter = (val) => {
    if (!val) return 0;
    // +/- 0.02% variation
    const noise = val * (Math.random() * 0.0004 - 0.0002);
    return val + noise;
};

export default function MultiAssetChart({ currentPrices }) {
    const [history, setHistory] = useState([]);
    const [visibility, setVisibility] = useState({
        BTC: true,
        ETH: true,
        EGLD: true,
        PAXG: true,
        MATIC: true,
        OP: true,
        ENTQ: true,
    });

    // --- Mise à jour de l'historique ---
    useEffect(() => {
        if (!currentPrices) return;

        setHistory((prev) => {
            const now = new Date();
            const timeStr = now.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });

            // Construction du point de donnée avec Jitter
            const newDataPoint = {
                time: timeStr,
                // On applique le jitter ICI pour que le graph l'enregistre
                BTC: jitter(currentPrices.BTC),
                ETH: jitter(currentPrices.ETH),
                EGLD: jitter(currentPrices.EGLD),
                PAXG: jitter(currentPrices.PAXG),
                MATIC: jitter(currentPrices.MATIC),
                OP: jitter(currentPrices.OP),
                ENTQ: currentPrices.ENTQ, // ENTQ a déjà son jitter backend si besoin
            };

            // ✅ MÉMOIRE ÉTENDUE : 500 points pour scroll
            const newHistory = [...prev, newDataPoint];
            if (newHistory.length > 500) {
                newHistory.shift();
            }
            return newHistory;
        });
    }, [currentPrices]);

    // --- Toggle des courbes ---
    const toggleVisibility = (symbol) => {
        setVisibility((prev) => ({
            ...prev,
            [symbol]: !prev[symbol],
        }));
    };

    return (
        <div className="multi-asset-chart-container" style={{ width: "100%", minHeight: "420px", padding: "1rem", background: "#111", borderRadius: "8px", border: "1px solid #333" }}>
            <h3 style={{ color: "#fff", marginBottom: "1rem" }}>📈 Marché & Navigation (Log Scale)</h3>

            {/* --- Contrôles de visibilité --- */}
            <div className="chart-controls" style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                {Object.keys(COLORS).map((symbol) => (
                    <label
                        key={symbol}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: COLORS[symbol],
                            cursor: "pointer",
                            fontWeight: "bold",
                            opacity: visibility[symbol] ? 1 : 0.5,
                            border: `1px solid ${visibility[symbol] ? COLORS[symbol] : "#444"}`,
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                            background: visibility[symbol] ? "rgba(255,255,255,0.05)" : "transparent"
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={visibility[symbol]}
                            onChange={() => toggleVisibility(symbol)}
                            style={{ display: "none" }}
                        />
                        {visibility[symbol] ? "👁️" : "xxxx"} {symbol}
                    </label>
                ))}
            </div>

            {/* --- Graphique Recharts --- */}
            <div style={{ width: "100%", minWidth: "100px" }}>
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={history}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="time" stroke="#666" fontSize={12} tick={{ fill: "#666" }} />

                        {/* ✅ Axe Gauche LOGARITHMIQUE pour voir BTC(90k) et ETH(3k) ensemble */}
                        <YAxis
                            yAxisId="left"
                            scale="log"
                            domain={['auto', 'auto']}
                            stroke="#888"
                            fontSize={12}
                            tick={{ fill: "#888" }}
                            tickFormatter={(val) => `$${Number(val).toLocaleString()}`}
                            allowDataOverflow
                        />

                        {/* Axe Droite (ENTQ) Linéaire */}
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="#FF007A"
                            fontSize={12}
                            tick={{ fill: "#FF007A" }}
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(val) => `$${val}`}
                        />

                        <Tooltip
                            contentStyle={{ backgroundColor: "#000", border: "1px solid #444", color: "#fff" }}
                            itemStyle={{ fontSize: "0.9rem" }}
                            formatter={(value) => [`$${Number(value).toFixed(2)}`, "Price"]}
                        />
                        <Legend verticalAlign="top" height={36} iconType="circle" />

                        {visibility.BTC && <Line yAxisId="left" type="monotone" dataKey="BTC" stroke={COLORS.BTC} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}
                        {visibility.ETH && <Line yAxisId="left" type="monotone" dataKey="ETH" stroke={COLORS.ETH} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}
                        {visibility.EGLD && <Line yAxisId="left" type="monotone" dataKey="EGLD" stroke={COLORS.EGLD} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}
                        {visibility.PAXG && <Line yAxisId="left" type="monotone" dataKey="PAXG" stroke={COLORS.PAXG} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}
                        {visibility.MATIC && <Line yAxisId="left" type="monotone" dataKey="MATIC" stroke={COLORS.MATIC} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}
                        {visibility.OP && <Line yAxisId="left" type="monotone" dataKey="OP" stroke={COLORS.OP} dot={false} strokeWidth={2} animationDuration={0} isAnimationActive={false} />}

                        {visibility.ENTQ && <Line yAxisId="right" type="monotone" dataKey="ENTQ" stroke={COLORS.ENTQ} dot={false} strokeWidth={3} animationDuration={0} isAnimationActive={false} />}

                        {/* ✅ Navigation Scrollbar */}
                        <Brush
                            dataKey="time"
                            height={30}
                            stroke="#FF007A"
                            fill="#1a1a1a"
                            tickFormatter={() => ""}
                        />

                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
